import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Archive, ChevronLeft, ChevronRight, Copy, ExternalLink, FileSpreadsheet, ImageOff, Trash2, Upload, X } from 'lucide-react'
import { announcementsApi, type Announcement, type AnnouncementPatch, type MemberSummary } from '../../lib/infoserviceApi.js'
import { memberQuotaText } from '../../lib/memberQuota.js'
import { isInMonth, monthGridDays, monthLabel, shiftMonth } from '../../lib/monthGrid.js'
import { isDayKey, isDayRange, shortDayLabel } from '../../lib/announcementGrid.js'
import { Spinner } from '../common/Spinner.js'
import { AnnouncementImagePicker } from './AnnouncementImagePicker.js'

interface DrawerProps {
  announcement: Announcement
  members: MemberSummary[]
  today: string
  canManage: boolean
  channelId: string | null
  /** Feuille Google synchronisée, pour « Ouvrir dans la feuille ». */
  sheetEditUrl?: string | null
  onClose: () => void
  onUpdated: (announcement: Announcement) => void
  onDuplicated: (announcement: Announcement) => void
  onDeleted: () => void
  /** Saisies non enregistrées : l'écran demande confirmation avant de quitter la fiche. */
  onDirtyChange?: (dirty: boolean) => void
}

interface FormState {
  organizationLabel: string
  eventLabel: string
  memberId: string
  hasText: boolean
  priority: boolean
  cancelled: boolean
  notes: string
}

function formOf(announcement: Announcement): FormState {
  return {
    organizationLabel: announcement.organizationLabel,
    eventLabel: announcement.eventLabel,
    memberId: announcement.memberId ?? '',
    hasText: announcement.hasText,
    priority: announcement.priority,
    cancelled: announcement.cancelled,
    notes: announcement.notes ?? '',
  }
}

/**
 * Annonce tenue par la feuille Google : seuls l'adhérent, la priorité et les
 * notes partent d'ici (le serveur refuserait le reste, que la synchronisation
 * écraserait de toute façon).
 */
function sameForm(a: FormState, b: FormState, sheetLinked: boolean): boolean {
  return JSON.stringify(patchOf(a, sheetLinked)) === JSON.stringify(patchOf(b, sheetLinked))
}

function patchOf(form: FormState, sheetLinked = false): AnnouncementPatch {
  if (sheetLinked) {
    return { memberId: form.memberId || null, priority: form.priority, notes: form.notes.trim() || null }
  }
  return {
    organizationLabel: form.organizationLabel.trim(),
    eventLabel: form.eventLabel.trim(),
    memberId: form.memberId || null,
    hasText: form.hasText,
    priority: form.priority,
    cancelled: form.cancelled,
    notes: form.notes.trim() || null,
  }
}

/**
 * Fiche d'une annonce : panneau qui recouvre la droite du tableau (décision de
 * l'exploitant). Les champs s'enregistrent par « Enregistrer » ; les jours,
 * l'image et les actions s'appliquent immédiatement, comme dans le tableau.
 */
export const AnnouncementDrawer = memo(function AnnouncementDrawer({
  announcement, members, today, canManage, channelId, sheetEditUrl, onClose, onUpdated, onDuplicated, onDeleted, onDirtyChange,
}: DrawerProps) {
  const [form, setForm] = useState<FormState>(() => formOf(announcement))
  const [busy, setBusy] = useState<string | null>(null)
  const [message, setMessage] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null)
  const [month, setMonth] = useState(() => (announcement.days.find(day => day >= today) ?? today).slice(0, 7))
  const [range, setRange] = useState({ from: today, to: today })
  const [picking, setPicking] = useState(false)
  const [noteDraft, setNoteDraft] = useState({ day: today, text: '' })
  const fileInput = useRef<HTMLInputElement>(null)

  const sheetLocked = announcement.sheetLinked
  const selectedMember = members.find(member => member.id === form.memberId) ?? null
  const baseline = useRef(announcement)

  // La même annonce relue (enregistrement, jour coché, image) : le formulaire
  // suit la nouvelle version, sauf s'il porte des saisies non enregistrées —
  // cocher un jour ne doit pas effacer une note en cours de frappe.
  useEffect(() => {
    const previous = baseline.current
    baseline.current = announcement
    setForm(current => (sameForm(current, formOf(previous), sheetLocked) ? formOf(announcement) : current))
    setMessage(null)
  }, [announcement, sheetLocked])

  const dirty = useMemo(() => !sameForm(form, formOf(announcement), sheetLocked), [announcement, form, sheetLocked])

  useEffect(() => {
    onDirtyChange?.(dirty)
  }, [dirty, onDirtyChange])
  const checked = useMemo(() => new Set(announcement.days), [announcement.days])
  const gridDays = useMemo(() => monthGridDays(month), [month])
  const archived = announcement.archivedAt !== null
  const readOnly = !canManage

  const run = useCallback(async (label: string, action: () => Promise<Announcement | void>, success?: string) => {
    setBusy(label)
    setMessage(null)
    try {
      const result = await action()
      if (result) onUpdated(result)
      if (success) setMessage({ tone: 'ok', text: success })
    } catch (cause) {
      setMessage({ tone: 'error', text: cause instanceof Error ? cause.message : 'Opération impossible' })
    } finally {
      setBusy(null)
    }
  }, [onUpdated])

  const set = useCallback(<K extends keyof FormState>(field: K, value: FormState[K]) => {
    setForm(current => ({ ...current, [field]: value }))
  }, [])

  const save = useCallback(() => {
    if (!form.organizationLabel.trim()) {
      setMessage({ tone: 'error', text: 'L’organisme est obligatoire.' })
      return
    }
    // L'adhérent ne part que s'il a changé : il vaut pour toute l'écriture de l'organisme.
    const patch = patchOf(form, sheetLocked)
    if (form.memberId === (announcement.memberId ?? '')) delete patch.memberId
    void run('save', () => announcementsApi.update(announcement.id, patch), 'Annonce enregistrée')
  }, [announcement.id, announcement.memberId, form, run, sheetLocked])

  const toggleDay = useCallback((day: string) => {
    void run('days', () => announcementsApi.setDays(announcement.id, checked.has(day) ? { remove: [day] } : { add: [day] }))
  }, [announcement.id, checked, run])

  const applyRange = useCallback((mode: 'add' | 'remove') => {
    if (range.to < range.from) {
      setMessage({ tone: 'error', text: 'La période doit finir après son début.' })
      return
    }
    void run('range', () => announcementsApi.setDays(announcement.id, { range: { ...range, mode } }),
      mode === 'add' ? `Cochée du ${shortDayLabel(range.from)} au ${shortDayLabel(range.to)}` : 'Période décochée')
  }, [announcement.id, range, run])

  const upload = useCallback((file: File | undefined) => {
    if (!file) return
    void run('image', () => announcementsApi.uploadImage(announcement.id, file), 'Image déposée dans INFOSERVICES/ANNONCES')
  }, [announcement.id, run])

  const archive = useCallback(() => {
    void run('archive', () => announcementsApi.update(announcement.id, { archived: !archived }), archived ? 'Annonce restaurée' : 'Annonce archivée')
  }, [announcement.id, archived, run])

  const duplicate = useCallback(async () => {
    setBusy('duplicate')
    try {
      onDuplicated(await announcementsApi.duplicate(announcement.id))
    } catch (cause) {
      setMessage({ tone: 'error', text: cause instanceof Error ? cause.message : 'Duplication impossible' })
    } finally {
      setBusy(null)
    }
  }, [announcement.id, onDuplicated])

  const remove = useCallback(async () => {
    if (!window.confirm(`Supprimer « ${announcement.organizationLabel} » ? Ses jours et commentaires disparaissent ; l’image reste sur le disque.`)) return
    setBusy('delete')
    try {
      await announcementsApi.remove(announcement.id)
      onDeleted()
    } catch (cause) {
      setMessage({ tone: 'error', text: cause instanceof Error ? cause.message : 'Suppression impossible' })
      setBusy(null)
    }
  }, [announcement.id, announcement.organizationLabel, onDeleted])

  const addNote = useCallback(() => {
    if (!noteDraft.text.trim()) return
    void run('note', async () => {
      const result = await announcementsApi.addNote(announcement.id, noteDraft)
      setNoteDraft(current => ({ ...current, text: '' }))
      return result
    })
  }, [announcement.id, noteDraft, run])

  const aspectWarning = announcement.imageWidth && announcement.imageHeight
    && Math.abs(announcement.imageWidth / announcement.imageHeight - 16 / 9) > 0.02

  const status = archived ? { tone: 'muted', text: 'Archivée' }
    : announcement.cancelled ? { tone: 'danger', text: 'Annulée' }
      : checked.has(today) ? (announcement.imageAvailable ? { tone: 'success', text: 'À l’antenne aujourd’hui' } : { tone: 'warning', text: 'Cochée aujourd’hui, sans image : ne passe pas' })
        : announcement.days.some(day => day > today) ? { tone: 'info', text: `Programmée — début le ${shortDayLabel(announcement.days.find(day => day > today)!)}` }
          : { tone: 'muted', text: 'Aucun jour à venir' }

  return (
    <aside className="announcement-drawer" aria-label={`Fiche de ${announcement.organizationLabel}`}>
      <header className="announcement-drawer__header">
        <div className="announcement-drawer__title">
          <span className="announcement-drawer__organization">{announcement.organizationLabel}</span>
          {announcement.eventLabel && <span className="announcement-drawer__event">{announcement.eventLabel}</span>}
          <span className={`announcement-drawer__status is-${status.tone}`}>{status.text}</span>
        </div>
        <button type="button" className="tp-icon-button" onClick={onClose} aria-label="Fermer la fiche"><X size={16} aria-hidden="true" /></button>
      </header>

      <div className="announcement-drawer__body">
        {message && (
          <div className={`tp-feedback ${message.tone === 'ok' ? 'tp-feedback--success' : 'tp-feedback--error'}`} role={message.tone === 'ok' ? 'status' : 'alert'}>
            {message.text}
          </div>
        )}

        <section className="announcement-drawer__section">
          <div className="announcement-drawer__image">
            {announcement.imageAvailable
              ? <img src={announcementsApi.imageUrl(announcement.id, announcement.updatedAt, 'large')} alt={`Annonce ${announcement.organizationLabel}`} width={1920} height={1080} decoding="async" />
              : (
                <div className="announcement-drawer__no-image">
                  <ImageOff size={24} aria-hidden="true" />
                  <span>{announcement.imagePath ? 'Image introuvable sur le disque' : 'Pas encore d’image'}</span>
                </div>
              )}
          </div>
          {announcement.imagePath && (
            <p className="announcement-drawer__path">
              {announcement.imagePath}
              {announcement.imageWidth && announcement.imageHeight ? ` · ${announcement.imageWidth}×${announcement.imageHeight}` : ''}
            </p>
          )}
          {aspectWarning && <p className="tp-feedback tp-feedback--warning">L’image n’est pas au format 16:9 : elle sera recadrée ou bordée à l’antenne.</p>}
          {!readOnly && (
            <div className="announcement-drawer__actions">
              <input ref={fileInput} type="file" accept="image/png,image/jpeg" hidden onChange={event => { upload(event.target.files?.[0]); event.target.value = '' }} />
              <button type="button" className="tp-button" disabled={busy !== null} onClick={() => fileInput.current?.click()}>
                {busy === 'image' ? <Spinner /> : <Upload size={14} aria-hidden="true" />} Importer une image
              </button>
              <button type="button" className="tp-button" disabled={busy !== null || !channelId} onClick={() => setPicking(true)}>Choisir dans INFOSERVICES</button>
              {announcement.imagePath && (
                <button type="button" className="tp-button" disabled={busy !== null} onClick={() => void run('image', () => announcementsApi.detachImage(announcement.id), 'Image détachée (le fichier reste sur le disque)')}>
                  Détacher
                </button>
              )}
            </div>
          )}
        </section>

        <section className="announcement-drawer__section">
          <h3 className="announcement-drawer__heading">Annonce</h3>
          {sheetLocked && (
            <div className="announcement-drawer__sheet" role="note">
              <FileSpreadsheet size={14} aria-hidden="true" />
              <span>
                {announcement.sheetRow ? <>Ligne {announcement.sheetRow} de la feuille Google. </> : <>Ligne disparue de la feuille Google. </>}
                Organisme, événement, « Texte », jours et annulation se modifient dans la feuille.
              </span>
              {sheetEditUrl && (
                <a className="tp-button" href={announcement.sheetRow ? `${sheetEditUrl}#range=A${announcement.sheetRow}` : sheetEditUrl} target="_blank" rel="noreferrer">
                  <ExternalLink size={14} aria-hidden="true" /> Ouvrir
                </a>
              )}
            </div>
          )}
          <fieldset className="announcement-drawer__fields" disabled={readOnly}>
            <label className="tp-form-field">
              <span className="tp-field-label">Organisme</span>
              <input className="tp-input" disabled={sheetLocked} value={form.organizationLabel} onChange={event => set('organizationLabel', event.target.value)} />
            </label>
            <label className="tp-form-field">
              <span className="tp-field-label">Événement</span>
              <input className="tp-input" disabled={sheetLocked} value={form.eventLabel} onChange={event => set('eventLabel', event.target.value)} />
            </label>
            <label className="tp-form-field announcement-drawer__wide">
              <span className="tp-field-label">Adhérent</span>
              <select className="tp-input" value={form.memberId} aria-describedby="announcement-member-quota" onChange={event => set('memberId', event.target.value)}>
                <option value="">D’après le nom de l’organisme</option>
                {members.map(member => <option key={member.id} value={member.id}>{member.name}</option>)}
              </select>
            </label>
            <p id="announcement-member-quota" className="announcement-drawer__hint announcement-drawer__wide">
              {selectedMember && <>{memberQuotaText(selectedMember)}. </>}
              {form.memberId !== (announcement.memberId ?? '')
                ? `Vaut pour toutes les annonces écrites « ${form.organizationLabel.trim() || announcement.organizationLabel} ».`
                : !selectedMember && 'Aucun adhérent reconnu pour cet organisme : choisissez-le, ou passez par Adhérents › À rattacher.'}
            </p>
          </fieldset>
          <fieldset className="announcement-drawer__checks" disabled={readOnly}>
            <Check label="Texte" hint="L’image contient du texte. Simple information." checked={form.hasText} disabled={sheetLocked} onChange={value => set('hasText', value)} />
            <Check label="Prioritaire" hint="Passe en tête juste après l’intro, puis de nouveau en tête à chaque tour de boucle." checked={form.priority} onChange={value => set('priority', value)} />
            <Check label="Annulée" hint={sheetLocked ? 'Nom en rouge dans la feuille : ne passe plus, même cochée.' : 'Ne passe plus, même cochée. Les jours restent visibles pour mémoire.'} checked={form.cancelled} disabled={sheetLocked} onChange={value => set('cancelled', value)} />
          </fieldset>
          <label className="tp-form-field">
            <span className="tp-field-label">Notes</span>
            <textarea className="tp-textarea" rows={2} value={form.notes} disabled={readOnly} onChange={event => set('notes', event.target.value)} />
          </label>
        </section>

        <section className="announcement-drawer__section">
          <h3 className="announcement-drawer__heading">
            Jours de diffusion <span className="announcement-drawer__count">{announcement.totalDays} jour{announcement.totalDays > 1 ? 's' : ''}</span>
          </h3>
          <div className="announcement-calendar">
            <div className="announcement-calendar__nav">
              <button type="button" className="tp-icon-button" onClick={() => setMonth(current => shiftMonth(current, -1))} aria-label="Mois précédent"><ChevronLeft size={14} aria-hidden="true" /></button>
              <span className="announcement-calendar__month">{monthLabel(month)}</span>
              <button type="button" className="tp-icon-button" onClick={() => setMonth(current => shiftMonth(current, 1))} aria-label="Mois suivant"><ChevronRight size={14} aria-hidden="true" /></button>
            </div>
            <div className="announcement-calendar__grid" role="grid" aria-label={`Jours cochés en ${monthLabel(month)}`}>
              {['L', 'M', 'M', 'J', 'V', 'S', 'D'].map((letter, index) => <span key={index} className="announcement-calendar__weekday" aria-hidden="true">{letter}</span>)}
              {gridDays.map(day => (
                <button
                  key={day}
                  type="button"
                  className={[
                    'announcement-calendar__day',
                    checked.has(day) ? 'is-checked' : '',
                    checked.has(day) && !announcement.imageAvailable ? 'is-missingImage' : '',
                    day === today ? 'is-today' : '',
                    isInMonth(day, month) ? '' : 'is-outside',
                  ].filter(Boolean).join(' ')}
                  aria-pressed={checked.has(day)}
                  aria-label={`${day}${checked.has(day) ? ', cochée' : ''}`}
                  disabled={readOnly || archived || sheetLocked || busy !== null}
                  onClick={() => toggleDay(day)}
                >
                  {Number(day.slice(8))}
                </button>
              ))}
            </div>
          </div>
          {!readOnly && !archived && !sheetLocked && (
            <div className="announcement-drawer__range">
              <label className="tp-form-field">
                <span className="tp-field-label">Du</span>
                <input className="tp-input" type="date" value={range.from} onChange={event => setRange(current => ({ ...current, from: event.target.value }))} />
              </label>
              <label className="tp-form-field">
                <span className="tp-field-label">Au</span>
                <input className="tp-input" type="date" value={range.to} onChange={event => setRange(current => ({ ...current, to: event.target.value }))} />
              </label>
              <button type="button" className="tp-button" disabled={busy !== null || !isDayRange(range.from, range.to)} onClick={() => applyRange('add')}>Cocher ces jours</button>
              <button type="button" className="tp-button" disabled={busy !== null || !isDayRange(range.from, range.to)} onClick={() => applyRange('remove')}>Décocher</button>
            </div>
          )}
          <p className="announcement-drawer__hint">
            {sheetLocked ? 'Les jours se colorent dans la feuille Google. ' : ''}
            Une annonce cochée un jour passe ce jour-là si elle a une image. Sans image, une alerte part la veille à 18:00.
          </p>
        </section>

        <section className="announcement-drawer__section">
          <h3 className="announcement-drawer__heading">Commentaires datés</h3>
          {announcement.dayNotes.length === 0
            ? <p className="announcement-drawer__hint">Aucun commentaire.</p>
            : (
              <ul className="announcement-drawer__notes">
                {announcement.dayNotes.map(note => (
                  <li key={note.id}>
                    <span className="announcement-drawer__note-day">{shortDayLabel(note.day)}</span>
                    <span>{note.text}</span>
                    {note.fromSheet && <span className="announcement-drawer__note-source">feuille</span>}
                    {!readOnly && !note.fromSheet && (
                      <button type="button" className="tp-icon-button" aria-label={`Supprimer le commentaire du ${note.day}`} disabled={busy !== null}
                        onClick={() => void run('note', () => announcementsApi.removeNote(announcement.id, note.id))}>
                        <X size={12} aria-hidden="true" />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          {!readOnly && (
            <div className="announcement-drawer__range">
              <label className="tp-form-field">
                <span className="tp-field-label">Jour</span>
                <input className="tp-input" type="date" value={noteDraft.day} onChange={event => setNoteDraft(current => ({ ...current, day: event.target.value }))} />
              </label>
              <label className="tp-form-field announcement-drawer__grow">
                <span className="tp-field-label">Commentaire</span>
                <input className="tp-input" value={noteDraft.text} maxLength={500} placeholder="ANNULÉ, reporté, changement de visuel…" onChange={event => setNoteDraft(current => ({ ...current, text: event.target.value }))} />
              </label>
              <button type="button" className="tp-button" disabled={busy !== null || !noteDraft.text.trim() || !isDayKey(noteDraft.day)} onClick={addNote}>Ajouter</button>
            </div>
          )}
        </section>

        <section className="announcement-drawer__section">
          <h3 className="announcement-drawer__heading">Historique de diffusion</h3>
          <p className="announcement-drawer__hint">
            {announcement.days.some(day => day <= today)
              ? 'Le relevé passage par passage arrivera avec le diaporama : l’antenne diffuse encore le fichier MP4 du jour.'
              : `Pas encore diffusée${announcement.firstDay ? ` — premier passage le ${shortDayLabel(announcement.firstDay)}` : ''}.`}
          </p>
        </section>

        {/* Actions rares, dans la fiche plutôt qu'à côté d'« Enregistrer ». */}
        {!readOnly && !sheetLocked && (
          <section className="announcement-drawer__section announcement-drawer__manage" aria-label="Gérer l’annonce">
            <button type="button" className="tp-button" disabled={busy !== null} onClick={archive}>
              <Archive size={14} aria-hidden="true" /> {archived ? 'Restaurer' : 'Archiver'}
            </button>
            <button type="button" className="tp-button" disabled={busy !== null || dirty} title={dirty ? 'Enregistrez d’abord les modifications' : undefined} onClick={() => void duplicate()}>
              <Copy size={14} aria-hidden="true" /> Dupliquer
            </button>
            <button type="button" className="tp-button tp-button--danger-outline" disabled={busy !== null} onClick={() => void remove()}>
              <Trash2 size={14} aria-hidden="true" /> Supprimer
            </button>
          </section>
        )}
      </div>

      {/* Enregistrer en bas à gauche, loin de la croix de fermeture (en haut à droite). */}
      {!readOnly && (
        <footer className="announcement-drawer__footer">
          <button type="button" className="tp-button tp-button--primary" disabled={!dirty || busy !== null} onClick={save}>
            {busy === 'save' && <Spinner />} Enregistrer
          </button>
          {dirty && <button type="button" className="tp-button" disabled={busy !== null} onClick={() => setForm(formOf(announcement))}>Annuler les modifications</button>}
          <span className="announcement-drawer__save-state" aria-live="polite">
            {dirty && <span className="announcement-drawer__unsaved">Non enregistré</span>}
          </span>
        </footer>
      )}

      {picking && channelId && (
        <AnnouncementImagePicker
          channelId={channelId}
          currentPath={announcement.imagePath}
          onClose={() => setPicking(false)}
          onPick={path => {
            setPicking(false)
            void run('image', () => announcementsApi.linkImage(announcement.id, path), 'Image rattachée')
          }}
        />
      )}
    </aside>
  )
})

const Check = memo(function Check({ label, hint, checked, disabled, onChange }: { label: string; hint: string; checked: boolean; disabled?: boolean; onChange: (value: boolean) => void }) {
  return (
    <label className={`announcement-drawer__check${disabled ? ' is-locked' : ''}`}>
      <input type="checkbox" checked={checked} disabled={disabled} onChange={event => onChange(event.target.checked)} />
      <span>
        <span className="announcement-drawer__check-label">{label}</span>
        <span className="announcement-drawer__check-hint">{hint}</span>
      </span>
    </label>
  )
})
